import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  List,
  ListItem,
  Typography,
  Paper,
  Divider,
  Stack,
  Avatar,
  Chip,
  Skeleton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { PersonAdd as PersonAddIcon, CheckCircle, Cancel, Close } from "@mui/icons-material";
import {
  useGetGuestUsers,
  useInviteGuest,
} from "../../../services/stagemode_api/moderator/moderator_api";
import SocketHandler from "../../../utils/socketHandler";
import { enqueueSnackbar } from "notistack";

const COLORS = {
  bgPanel: "#141414",
  bgAlt: "#1b1b1b",
  border: "rgba(255,255,255,0.12)",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.74)",
  red: "#E50914",
  redHover: "#b20710",
};

const GuestManagement: React.FC<{ bookingId: string }> = ({ bookingId }) => {
  const [invitingGuest, setInvitingGuest] = useState(false);
  const socketSubscribedRef = useRef(false);
  const [invitationDialog, setInvitationDialog] = useState<{
    open: boolean;
    type: 'accepted' | 'rejected';
    guestName: string;
  }>({
    open: false,
    type: 'accepted',
    guestName: '',
  });

  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data: guestUsers,
    isLoading: guestUserLoading,
    refetch: refetchGuestUsers,
  } = useGetGuestUsers({
    params: { id: bookingId ?? "" },
    key: ["guest-users", bookingId],
  });

  const { mutateAsync, isLoading: inviteLoading } = useInviteGuest(
    bookingId ?? ""
  );

  useEffect(() => {
    if (socketSubscribedRef.current || !bookingId) return;

    const socketHandler = SocketHandler.getInstance();
    socketHandler.subscribeToStageEvent(bookingId, (message) => {
      console.log("Guest management socket message:", message);
      if (message?.notification_type === "user-joined") {
        refetchGuestUsers();
      } else if (message?.notification_type === "invite-accepted") {
        const guestName = message?.guest_name ;
        setInvitationDialog({
          open: true,
          type: 'accepted',
          guestName,
        });
        refetchGuestUsers();
      } else if (message?.notification_type === "invite-rejected") {
        const guestName = message?.guest_name ;
        setInvitationDialog({
          open: true,
          type: 'rejected',
          guestName,
        });
      }
    });

    socketSubscribedRef.current = true;
    return () => {
      socketHandler.unsubscribeToStageEvent(bookingId);
      socketSubscribedRef.current = false;
    };
  }, [bookingId, refetchGuestUsers]);

  const sendInvitation = async (userId: string | number) => {
    if (!invitingGuest) return;
    try {
      await mutateAsync({ userId });
      enqueueSnackbar("Guest invited successfully", { variant: "success" });
      setInvitingGuest(false);
      refetchGuestUsers();
    } catch (err) {
      enqueueSnackbar("Failed to invite guest", { variant: "error" });
      setInvitingGuest(false);
    }
  };

  // helpers
  const totalGuests = Array.isArray(guestUsers?.data) ? guestUsers?.data?.length : 0;

  const handleCloseInvitationDialog = () => {
    setInvitationDialog(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ width: "100%", color: COLORS.text, display: "flex", flexDirection: "column" }}>
      {/* Header Card */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: COLORS.bgPanel,
          border: "1px solid",
          borderColor: COLORS.border,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          sx={{ flexWrap: "wrap" }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Typography variant={smDown ? "subtitle1" : "h6"} sx={{ fontWeight: 800 }}>
              Guest Management
            </Typography>
            <Chip
              size="small"
              label={
                guestUserLoading ? "Loading…" : `${totalGuests} ${totalGuests === 1 ? "guest" : "guests"}`
              }
              sx={{
                bgcolor: "rgba(229,9,20,0.18)",
                border: `1px solid ${COLORS.red}`,
                color: COLORS.text,
                height: 24,
              }}
            />
          </Stack>

          <Tooltip title={invitingGuest ? "Click a guest below to invite" : "Invite a guest to the stage"}>
            <span>
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => setInvitingGuest(true)}
                disabled={inviteLoading}
                sx={{
                  borderRadius: 2,
                  fontWeight: 800,
                  textTransform: "none",
                  bgcolor: COLORS.red,
                  "&:hover": { bgcolor: COLORS.redHover },
                  minWidth: 160,
                }}
              >
                {invitingGuest ? "Select a guest…" : "Invite Guest"}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      {/* List Card */}
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          p: 0,
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: COLORS.bgPanel,
          border: "1px solid",
          borderColor: COLORS.border,
          display: "flex",
          flexDirection: "column",
          minHeight: 140,
          maxHeight: smDown ? 360 : 440,
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: COLORS.bgAlt,
            borderBottom: "1px solid",
            borderColor: COLORS.border,
          }}
        >
          <Typography variant="subtitle2" sx={{ color: COLORS.textMuted, letterSpacing: "0.03em" }}>
            Audience Currently Viewing
          </Typography>
        </Box>

        <Box sx={{ p: 2, pt: 0, overflowY: "auto", flex: 1 }}>
          {guestUserLoading ? (
            <Stack spacing={1.5} sx={{ pt: 2 }}>
              {[...Array(4)].map((_, i) => (
                <Stack key={i} direction="row" alignItems="center" spacing={1.5}>
                  <Skeleton variant="circular" width={36} height={36} sx={{ bgcolor: COLORS.bgAlt }} />
                  <Skeleton variant="text" width="40%" height={24} sx={{ bgcolor: COLORS.bgAlt }} />
                  <Box sx={{ ml: "auto" }}>
                    <Skeleton variant="rectangular" width={88} height={32} sx={{ bgcolor: COLORS.bgAlt }} />
                  </Box>
                </Stack>
              ))}
            </Stack>
          ) : totalGuests ? (
            <List disablePadding>
              {guestUsers?.data?.map((guest: any, idx: number) => {
                const userId = guest?.user?.id;
                const display = guest?.guest_name ?? guest?.user?.username ?? "Guest";
                const initials = String(display).trim().slice(0, 2).toUpperCase();

                return (
                  <React.Fragment key={`${userId ?? idx}-${display}`}>
                    <ListItem
                      sx={{
                        px: 1,
                        py: 1,
                        borderRadius: 1,
                        "&:hover": { bgcolor: "rgba(255,255,255,0.04)" },
                        gap: 1.25,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          fontSize: 14,
                          bgcolor: COLORS.bgAlt,
                          border: "1px solid",
                          borderColor: COLORS.border,
                        }}
                        src={guest?.user?.profile_image || ""}
                      >
                        {initials}
                      </Avatar>

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>
                          {display}
                        </Typography>
                        {guest?.user?.username && (
                          <Typography variant="caption" sx={{ color: COLORS.textMuted }}>
                            @{guest.user.username}
                          </Typography>
                        )}
                      </Box>

                      {invitingGuest && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={inviteLoading}
                          onClick={() => sendInvitation(userId)}
                          sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 700,
                            borderColor: COLORS.red,
                            color: COLORS.text,
                            "&:hover": {
                              borderColor: COLORS.red,
                              bgcolor: "rgba(229,9,20,0.08)",
                            },
                          }}
                        >
                          Invite
                        </Button>
                      )}
                    </ListItem>

                    {idx !== totalGuests - 1 && (
                      <Divider sx={{ my: 1, borderColor: COLORS.border }} />
                    )}
                  </React.Fragment>
                );
              })}
            </List>
          ) : (
            <Box
              sx={{
                display: "grid",
                placeItems: "center",
                textAlign: "center",
                py: 6,
                color: COLORS.textMuted,
              }}
            >
              <Typography variant="body2">No guests yet.</Typography>
              <Typography variant="caption">
                Share your StageLink to start receiving viewers.
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Invitation Response Dialog */}
      <Dialog
        open={invitationDialog.open}
        onClose={handleCloseInvitationDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: COLORS.bgPanel,
            border: "1px solid",
            borderColor: COLORS.border,
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: COLORS.text,
            borderBottom: "1px solid",
            borderColor: COLORS.border,
            bgcolor: COLORS.bgAlt,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: invitationDialog.type === 'accepted' 
                  ? "rgba(76, 175, 80, 0.2)" 
                  : "rgba(244, 67, 54, 0.2)",
                border: "1px solid",
                borderColor: invitationDialog.type === 'accepted' 
                  ? "rgba(76, 175, 80, 0.4)" 
                  : "rgba(244, 67, 54, 0.4)",
              }}
            >
              {invitationDialog.type === 'accepted' ? (
                <CheckCircle sx={{ color: "#4CAF50", fontSize: 24 }} />
              ) : (
                <Cancel sx={{ color: "#F44336", fontSize: 24 }} />
              )}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {invitationDialog.type === 'accepted' ? 'Invitation Accepted!' : 'Invitation Declined'}
            </Typography>
          </Stack>
          <IconButton
            onClick={handleCloseInvitationDialog}
            sx={{ color: COLORS.textMuted, "&:hover": { color: COLORS.text } }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="body1" sx={{ color: COLORS.text, fontWeight: 500 }}>
              {invitationDialog.guestName}
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.textMuted, lineHeight: 1.6 }}>
              {invitationDialog.type === 'accepted' 
                ? `has accepted your invitation to join the stage. They will now be able to participate in the live session.`
                : `has declined your invitation to join the stage. You can invite them again later if needed.`
              }
            </Typography>
          </Stack>
        </DialogContent>
        
        <DialogActions sx={{ 
          borderTop: "1px solid", 
          borderColor: COLORS.border,
          bgcolor: COLORS.bgAlt,
          px: 3,
          py: 2,
        }}>
          <Button
            onClick={handleCloseInvitationDialog}
            variant="contained"
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              textTransform: "none",
              bgcolor: COLORS.red,
              "&:hover": { bgcolor: COLORS.redHover },
              minWidth: 100,
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GuestManagement;
